import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Mail, MessageCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import caseStudies, { caseStudyOrder } from "@/data/caseStudies";
import portfolio from "@/data/portfolio";

export default function CaseStudy() {
  const { slug } = useParams();
  const cs = caseStudies[slug];
  if (!cs) return <Navigate to="/" replace />;

  const idx = caseStudyOrder.indexOf(slug);
  const nextSlug =
    idx >= 0 ? caseStudyOrder[(idx + 1) % caseStudyOrder.length] : null;
  const next = nextSlug ? caseStudies[nextSlug] : null;

  return (
    <main
      data-testid={`case-study-${cs.id}`}
      className="bg-white text-[#111] font-sans-body"
    >
      <Navbar />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link
            to="/#career"
            data-testid="case-study-back"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-gray-500 hover:text-black transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All projects
          </Link>

          <div className="mt-8 flex items-center gap-3">
            <span className="inline-block w-10 h-px bg-black/40" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
              {cs.eyebrow}
            </span>
          </div>

          <h1 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-black leading-[1.0]">
            {cs.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
            <span className="text-black">{cs.subtitle}</span>
            <span>·</span>
            <span>{cs.years}</span>
            {cs.location && (
              <>
                <span>·</span>
                <span>{cs.location}</span>
              </>
            )}
          </div>

          <p className="mt-8 max-w-3xl text-xl md:text-2xl font-light text-gray-700 leading-relaxed">
            {cs.headline}
          </p>
        </div>
      </section>

      {/* Hero media — video container if present, else image */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-[2rem] md:rounded-[3rem] bg-[#F5F5F7]"
            data-testid={`case-study-${cs.id}-hero-media`}
          >
            {cs.hero.video ? (
              <video
                src={cs.hero.video}
                poster={cs.hero.poster || cs.hero.image}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-[55vh] sm:h-[65vh] md:h-[78vh] object-cover"
              />
            ) : (
              <img
                src={cs.hero.image}
                alt={cs.title}
                className={`w-full h-[55vh] sm:h-[65vh] md:h-[78vh] ${
                  cs.hero.imageFit === "contain"
                    ? "object-contain p-8 md:p-16 bg-white"
                    : "object-cover"
                }`}
                loading="eager"
                decoding="async"
              />
            )}
          </motion.div>
        </div>
      </section>

      {/* Executive summary */}
      {cs.summary?.length > 0 && (
        <CaseSection eyebrow="Executive summary">
          <div className="space-y-6">
            {cs.summary.map((p, i) => (
              <p
                key={i}
                className="text-lg md:text-xl text-gray-700 font-light leading-relaxed max-w-4xl"
              >
                {p}
              </p>
            ))}
          </div>
        </CaseSection>
      )}

      {/* Featured video (optional — NEL uses this) */}
      {cs.featuredVideo?.url && (
        <section className="py-12 md:py-20 border-t hairline bg-black" data-testid={`featured-video-${cs.id}`}>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-8 h-px bg-white/40" />
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                {cs.featuredVideo.eyebrow || "Vision film"}
              </span>
            </div>
            {cs.featuredVideo.title && (
              <h2 className="font-serif-display text-2xl md:text-3xl lg:text-4xl font-light tracking-tight text-white leading-[1.15] mb-8 max-w-3xl">
                {cs.featuredVideo.title}
              </h2>
            )}
            <div className="overflow-hidden rounded-2xl md:rounded-[2rem] border border-white/10 bg-black">
              <video
                src={cs.featuredVideo.url}
                poster={cs.featuredVideo.poster || undefined}
                controls
                playsInline
                preload="metadata"
                className="block w-full h-auto"
              />
            </div>
          </div>
        </section>
      )}

      {/* Mission + Metrics side-by-side */}
      <section className="py-16 md:py-24 border-t hairline bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <SectionEyebrow>Mission</SectionEyebrow>
              <h2 className="mt-5 font-serif-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-black leading-[1.1]">
                {cs.mission}
              </h2>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {cs.metrics.map((m, i) => (
                  <div
                    key={i}
                    data-testid={`case-metric-${cs.id}-${i}`}
                    className="bg-white border hairline rounded-2xl p-5 md:p-6"
                  >
                    <div className="font-serif-display text-3xl md:text-4xl text-black tracking-tight">
                      {m.value}
                    </div>
                    <div className="mt-2 text-xs md:text-sm text-gray-500">
                      {m.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem (optional — Ryde TV uses this) */}
      {cs.ecosystem?.chapters?.length > 0 && (
        <section className="py-20 md:py-28 border-t hairline bg-black text-white" data-testid={`ecosystem-${cs.id}`}>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-white/40" />
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                {cs.ecosystem.eyebrow}
              </span>
            </div>
            {cs.ecosystem.headline && (
              <h2 className="mt-6 font-serif-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] max-w-4xl">
                {cs.ecosystem.headline}
              </h2>
            )}

            <div className="mt-14 md:mt-20 space-y-20 md:space-y-28">
              {cs.ecosystem.chapters.map((ch, i) => (
                <EcosystemChapter key={i} chapter={ch} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Responsibilities */}
      {cs.responsibilities?.length > 0 && (
        <CaseSection eyebrow="Key responsibilities">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
            {cs.responsibilities.map((r, i) => (
              <motion.li
                key={r}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className="flex items-start gap-3 text-base md:text-lg text-gray-700 font-light leading-relaxed"
              >
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-black flex-shrink-0" />
                {r}
              </motion.li>
            ))}
          </ul>
        </CaseSection>
      )}

      {/* Achievements */}
      {cs.achievements?.length > 0 && (
        <CaseSection eyebrow="Achievements" bg="bg-[#FAFAFA]">
          <ul className="space-y-4 max-w-4xl">
            {cs.achievements.map((a, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className="text-base md:text-lg text-gray-800 font-light leading-relaxed flex items-start gap-4"
              >
                <span className="mt-2 font-serif-display text-sm text-gray-400 w-6 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{a}</span>
              </motion.li>
            ))}
          </ul>
        </CaseSection>
      )}

      {/* Scope tags */}
      {cs.scope?.length > 0 && (
        <CaseSection eyebrow="Leadership scope">
          <div className="flex flex-wrap gap-2 md:gap-3">
            {cs.scope.map((s) => (
              <span
                key={s}
                data-testid={`case-scope-${cs.id}-${s.replace(/\s+/g, "-").toLowerCase()}`}
                className="rounded-full bg-[#F5F5F7] border hairline px-5 py-2 text-sm text-gray-800 hover:border-black/40 transition-colors"
              >
                {s}
              </span>
            ))}
          </div>
        </CaseSection>
      )}

      {/* Timeline (optional) */}
      {cs.timeline?.length > 0 && (
        <CaseSection eyebrow="Timeline" bg="bg-[#FAFAFA]">
          <ol className="relative border-l hairline pl-6 md:pl-10 space-y-8">
            {cs.timeline.map((t, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[34px] md:-left-[44px] top-2 w-2.5 h-2.5 rounded-full bg-black ring-4 ring-white" />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-8">
                  <div className="md:col-span-2">
                    <span className="font-serif-display text-2xl text-black">{t.year}</span>
                  </div>
                  <div className="md:col-span-10">
                    <h3 className="text-lg text-black font-medium">{t.milestone}</h3>
                    {t.desc && (
                      <p className="mt-1 text-sm md:text-base text-gray-600 font-light">
                        {t.desc}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </CaseSection>
      )}

      {/* Lessons learned (optional) */}
      {cs.lessonsLearned?.length > 0 && (
        <CaseSection eyebrow="Lessons learned">
          <ul className="space-y-4 max-w-4xl">
            {cs.lessonsLearned.map((l, i) => (
              <li
                key={i}
                className="text-base md:text-lg text-gray-700 font-light leading-relaxed border-l hairline pl-5"
              >
                {l}
              </li>
            ))}
          </ul>
        </CaseSection>
      )}

      {/* Key takeaway (closing reflection) */}
      {cs.keyTakeaway && (
        <section className="py-20 md:py-28 border-t hairline bg-black text-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-white/40" />
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                Key takeaway
              </span>
            </div>
            <p
              data-testid={`case-key-takeaway-${cs.id}`}
              className="mt-8 font-serif-display text-2xl md:text-3xl lg:text-4xl font-light leading-[1.3] tracking-tight max-w-5xl"
            >
              {cs.keyTakeaway}
            </p>
          </div>
        </section>
      )}

      {/* Gallery (optional — render only if items provided) */}
      {cs.gallery?.length > 0 && (
        <CaseSection eyebrow="Media gallery" bg="bg-[#FAFAFA]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cs.gallery.map((g, i) => (
              <figure
                key={i}
                className="relative overflow-hidden rounded-2xl bg-[#F5F5F7] aspect-[4/3]"
              >
                <img
                  src={g.src}
                  alt={g.caption || cs.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                {g.caption && (
                  <figcaption className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/55 to-transparent text-white text-xs md:text-sm">
                    {g.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </CaseSection>
      )}

      {/* Closing CTA + Next case study */}
      <section className="py-20 md:py-28 border-t hairline bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div>
              <SectionEyebrow>Continue the conversation</SectionEyebrow>
              <h3 className="mt-5 font-serif-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-black leading-[1.1]">
                Let's build something exceptional.
              </h3>
              <p className="mt-5 max-w-xl text-base md:text-lg text-gray-700 font-light leading-relaxed">
                Strategic mandates, board roles, partnerships, investment opportunities and introductions within the equestrian industry are always welcome.
              </p>
              <p className="mt-4 max-w-xl text-base md:text-lg text-gray-700 font-light leading-relaxed">
                Open to conversations with serious operators, investors, media groups, sports organisations and industry stakeholders.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/#contact"
                  data-testid={`case-cta-contact-${cs.id}`}
                  className="inline-flex items-center gap-2 bg-black text-white hover:bg-[#1a1a1a] rounded-full px-7 py-3.5 text-sm font-medium transition-colors"
                >
                  <Mail className="w-4 h-4" /> Contact Pablo
                </Link>
                <a
                  href={portfolio.contact.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-black border border-gray-200 hover:border-black rounded-full px-7 py-3.5 text-sm font-medium transition-all"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
            </div>

            {next && (
              <Link
                to={`/projects/${next.id}`}
                data-testid={`case-next-${next.id}`}
                className="group block bg-[#F5F5F7] border hairline rounded-3xl overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-black/5 transition-all duration-500"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={next.hero.image}
                    alt={next.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
                    Next project
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-4">
                    <h4 className="font-serif-display text-2xl md:text-3xl text-black tracking-tight">
                      {next.title}
                    </h4>
                    <ArrowRight className="w-5 h-5 flex-shrink-0 transition-transform group-hover:translate-x-1" />
                  </div>
                  <p className="mt-2 text-sm text-gray-600">{next.subtitle}</p>
                </div>
              </Link>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function CaseSection({ eyebrow, children, bg = "bg-white" }) {
  return (
    <section className={`py-16 md:py-24 border-t hairline ${bg}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <SectionEyebrow>{eyebrow}</SectionEyebrow>
        <div className="mt-8 md:mt-10">{children}</div>
      </div>
    </section>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div className="flex items-center gap-3">
      <span className="inline-block w-8 h-px bg-black/40" />
      <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
        {children}
      </span>
    </div>
  );
}

function EcosystemChapter({ chapter, index }) {
  const chapterNumber = String(index + 1).padStart(2, "0");
  const reverse = index % 2 === 1; // alternate sides on desktop

  // Hero layout — full-bleed image with copy underneath
  if (chapter.layout === "horizontal-hero") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        data-testid={`ecosystem-chapter-${index}`}
      >
        <div className="flex items-center gap-4 mb-6">
          <span className="font-serif-display text-2xl md:text-3xl text-white/40 tabular-nums">
            {chapterNumber}
          </span>
          <span className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
            {chapter.label}
          </span>
        </div>
        <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] max-w-3xl">
          {chapter.title}
        </h3>
        <p className="mt-5 text-base md:text-lg text-gray-300 font-light leading-relaxed max-w-3xl">
          {chapter.body}
        </p>
        <div className="mt-10 overflow-hidden rounded-2xl md:rounded-[2rem] border border-white/10 bg-white/[0.04]">
          <img
            src={chapter.image}
            alt={chapter.alt || chapter.title}
            className="w-full h-auto object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      </motion.div>
    );
  }

  // Vertical layout — mobile mockup (phone-style frame) + copy
  if (chapter.layout === "vertical") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        data-testid={`ecosystem-chapter-${index}`}
        className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
      >
        <div className={`lg:col-span-7 order-2 ${reverse ? "lg:order-2" : "lg:order-1"}`}>
          <div className="flex items-center gap-4 mb-6">
            <span className="font-serif-display text-2xl md:text-3xl text-white/40 tabular-nums">
              {chapterNumber}
            </span>
            <span className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
              {chapter.label}
            </span>
          </div>
          <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1]">
            {chapter.title}
          </h3>
          <p className="mt-5 text-base md:text-lg text-gray-300 font-light leading-relaxed max-w-xl">
            {chapter.body}
          </p>
        </div>
        <div className={`lg:col-span-5 order-1 ${reverse ? "lg:order-1" : "lg:order-2"} flex justify-center`}>
          {/* Phone-style frame */}
          <div className="relative mx-auto">
            <div className="absolute -inset-4 rounded-[3rem] bg-gradient-to-b from-white/5 to-transparent blur-2xl pointer-events-none" />
            <div className="relative w-[230px] sm:w-[260px] md:w-[280px] rounded-[2.4rem] border-[8px] border-[#0a0a0a] bg-black shadow-2xl shadow-black/40 overflow-hidden">
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-2xl z-10" aria-hidden />
              <img
                src={chapter.image}
                alt={chapter.alt || chapter.title}
                className="block w-full h-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Default horizontal split — copy left, image right (alternates)
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`ecosystem-chapter-${index}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
    >
      <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : "lg:order-1"}`}>
        <div className="flex items-center gap-4 mb-6">
          <span className="font-serif-display text-2xl md:text-3xl text-white/40 tabular-nums">
            {chapterNumber}
          </span>
          <span className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
            {chapter.label}
          </span>
        </div>
        <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1]">
          {chapter.title}
        </h3>
        <p className="mt-5 text-base md:text-lg text-gray-300 font-light leading-relaxed max-w-xl">
          {chapter.body}
        </p>
      </div>
      <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : "lg:order-2"}`}>
        <div className="overflow-hidden rounded-2xl md:rounded-3xl border border-white/10 bg-white/[0.04]">
          <img
            src={chapter.image}
            alt={chapter.alt || chapter.title}
            className="w-full h-auto object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </motion.div>
  );
}
